import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormControl, Select, MenuItem, InputLabel, makeStyles } from '@material-ui/core';
import Errors from 'components/shared/Errors';
import Main from 'components/templates/Main';
import PATHS from 'constants/paths';
import { hasHosted } from 'selectors/user';
import ComplaintTabs from '../components/ComplaintTabs';
import ComplaintsList from '../components/ComplaintsList';
import { fetchHostComplaintsRequest, complaintsReset } from '../redux/actionCreators';
import * as selectors from '../redux/selectors';
import { formatCottageLabel } from '../constants';

const STATUSES = [
  { value: 'all', labelId: 'complaints.filter.all', defaultMessage: 'All' },
  {
    value: 'pending_host',
    labelId: 'complaints.filter.pendingHost',
    defaultMessage: 'Awaiting your response',
  },
  {
    value: 'pending_guest',
    labelId: 'complaints.filter.pendingGuest',
    defaultMessage: 'Awaiting guest',
  },
  { value: 'accepted', labelId: 'complaints.filter.accepted', defaultMessage: 'Accepted' },
  { value: 'escalated', labelId: 'complaints.filter.escalated', defaultMessage: 'Escalated' },
  { value: 'resolved', labelId: 'complaints.filter.resolved', defaultMessage: 'Resolved' },
  { value: 'withdrawn', labelId: 'complaints.filter.withdrawn', defaultMessage: 'Withdrawn' },
];

const useStyles = makeStyles((theme) => ({
  wrap: {
    padding: theme.spacing(2, 1),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2),
    },
  },
  filter: {
    marginBottom: theme.spacing(2),
    minWidth: 200,
  },
}));

const HostComplaintsListContainer = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const intl = useIntl();
  const error = useSelector(selectors.error);
  const isLoading = useSelector(selectors.hostIsLoading);
  const list = useSelector(selectors.hostList);
  const isHost = useSelector(hasHosted);
  const [statusFilter, setStatusFilter] = useState('all');
  const [cottageFilter, setCottageFilter] = useState('all');

  const cottages = useMemo(() => {
    if (!list) return [];
    const map = {};
    list.forEach((c) => {
      const cottage = c.booking?.Cottage;
      if (cottage?.id && !map[cottage.id]) {
        map[cottage.id] = formatCottageLabel(cottage);
      }
    });
    return Object.entries(map).map(([id, name]) => ({ id: Number(id), name }));
  }, [list]);

  const filteredList = useMemo(() => {
    if (!list) return list;
    return list.filter((c) => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (cottageFilter !== 'all' && c.booking?.Cottage?.id !== cottageFilter) return false;
      return true;
    });
  }, [list, statusFilter, cottageFilter]);

  const emptyMessage = useMemo(() => {
    const hasFilter = statusFilter !== 'all' || cottageFilter !== 'all';
    if (!hasFilter) return null;
    return intl.formatMessage({
      id: 'complaints.noComplaintsFiltered',
      defaultMessage: 'No complaints match the selected filters',
    });
  }, [statusFilter, cottageFilter, intl]);

  React.useEffect(() => {
    if (isHost) dispatch(fetchHostComplaintsRequest());
    return () => dispatch(complaintsReset());
  }, []);

  if (!isHost) return <Redirect to={PATHS.complaints.root} />;

  return (
    <Main title={<FormattedMessage id="main.complaints" defaultMessage="Complaints" />}>
      <ComplaintTabs tab={1} showHostTab />
      <div className={classes.wrap}>
        <FormControl variant="outlined" className={classes.filter}>
          <InputLabel>
            <FormattedMessage id="complaints.filter.label" defaultMessage="Status" />
          </InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label={intl.formatMessage({ id: 'complaints.filter.label', defaultMessage: 'Status' })}
          >
            {STATUSES.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                <FormattedMessage id={s.labelId} defaultMessage={s.defaultMessage} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {cottages.length > 1 && (
          <FormControl variant="outlined" className={classes.filter}>
            <InputLabel>
              <FormattedMessage id="complaints.filter.cottage" defaultMessage="Listing" />
            </InputLabel>
            <Select
              value={cottageFilter}
              onChange={(e) => setCottageFilter(e.target.value)}
              label={intl.formatMessage({
                id: 'complaints.filter.cottage',
                defaultMessage: 'Listing',
              })}
            >
              <MenuItem value="all">
                <FormattedMessage id="complaints.filter.all" defaultMessage="All" />
              </MenuItem>
              {cottages.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <ComplaintsList
          list={filteredList}
          isLoading={isLoading}
          basePath={PATHS.hostComplaints.root}
          view="host"
          emptyMessage={emptyMessage}
        />
        <Errors errors={error} />
      </div>
    </Main>
  );
};

export default HostComplaintsListContainer;
